import { Component, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, catchError, finalize, map, of, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService, debouncedServerSearch } from '@card-software/nexus-utilities';

import { EnumService } from '../../../services/enum.service';
import { RemissionGuideProxyService } from '../../../proxies/remission-guide.proxy.service';
import { RemissionGuideDto, RemissionGuideStatus } from '../../../models/remission-guide/remission-guide';
import { RemissionGuideSearchCommand } from '../../../models/remission-guide/remission-guide-search';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Business, BusinessEntityType } from '../../../models/finance/business';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';

interface RemissionGuideFilters {
  number?: string;
  status?: RemissionGuideStatus | { value: RemissionGuideStatus } | null;
  issuedDateFrom?: Date | null;
  issuedDateTo?: Date | null;
  remitterReference?: string | { id?: string } | null;
  consigneeReference?: string | { id?: string } | null;
}

@Component({
  selector: 'app-remission-guides',
  templateUrl: './remission-guides.component.html',
  styleUrls: ['./remission-guides.component.scss'],
})
export class RemissionGuidesComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly proxyService = inject(RemissionGuideProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);

  private readonly destroy$ = new Subject<void>();
  private appliedFilters: RemissionGuideFilters = {};

  // State signals
  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  remissionGuides = signal<RemissionGuideDto[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'remission-guides-filters', config: this.filterConfig });

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  // Business autocomplete suggestions
  remitterSuggestions = signal<Business[]>([]);
  consigneeSuggestions = signal<Business[]>([]);

  // Enums
  remissionGuideStatus = signal<EnumValues>({});
  enumsReady = signal(false);

  // Computed: Status options for filter
  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.remissionGuideStatus();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  // Role-based permissions
  canCreate = computed(() => {
    return AuthService.hasRole(UserGroup.RemissionGuideEditor) || AuthService.hasRole(UserGroup.RemissionGuideAdmin);
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    // Load enum values
    effect(
      () => {
        const statusesValue = this.enumService.getEnum('RemissionGuideStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.remissionGuideStatus.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    // Initialize filter config when language and enums are ready
    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    // The dynamic-filters panel renders the autocomplete, so there is no element for
    // ServerSearchDirective: the shared operator is wired by hand and released with the component.
    this.remitterQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchBusinesses(query, [BusinessEntityType.Provider, BusinessEntityType.InternalCompany])),
        takeUntil(this.destroy$),
      )
      .subscribe((remitters) => this.remitterSuggestions.set(remitters));

    this.consigneeQueries$
      .pipe(
        debouncedServerSearch((query) => this.searchBusinesses(query, [BusinessEntityType.Client, BusinessEntityType.Consignee])),
        takeUntil(this.destroy$),
      )
      .subscribe((consignees) => this.consigneeSuggestions.set(consignees));

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  guideStrict(guide: unknown): RemissionGuideDto {
    return guide as RemissionGuideDto;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('RemissionGuide_Number', 'Number'),
          placeholder: translate('RemissionGuide_Enter_Number', 'Enter number'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: translate('RemissionGuide_Status', 'Status'),
          placeholder: translate('RemissionGuide_Select_Status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'remitterReference',
          label: translate('RemissionGuide_Remitter', 'Remitter'),
          placeholder: translate('RemissionGuide_Search_Remitter', 'Search remitter...'),
          autocompleteConfig: {
            suggestions: this.remitterSuggestions(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.remitterQueries$.next(event.query),
            resolveById: (id: string) => this.resolveBusiness(id),
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'consigneeReference',
          label: translate('RemissionGuide_Consignee', 'Consignee'),
          placeholder: translate('RemissionGuide_Search_Consignee', 'Search consignee...'),
          autocompleteConfig: {
            suggestions: this.consigneeSuggestions(),
            optionLabel: 'name',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.consigneeQueries$.next(event.query),
            resolveById: (id: string) => this.resolveBusiness(id),
            forceSelection: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'issuedDateFrom',
          label: translate('RemissionGuide_Issued_Date_From', 'Issued Date From'),
          placeholder: translate('RemissionGuide_Select_Start_Date', 'Select start date'),
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'issuedDateTo',
          label: translate('RemissionGuide_Issued_Date_To', 'Issued Date To'),
          placeholder: translate('RemissionGuide_Select_End_Date', 'Select end date'),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveBusiness(id: string): Observable<Business | null> {
    return this.businessProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private readonly remitterQueries$ = new Subject<string>();
  private readonly consigneeQueries$ = new Subject<string>();

  private searchBusinesses(query: string, businessTypes: BusinessEntityType[]): Observable<Business[]> {
    return this.businessProxyService.search({ name: query, businessTypes }).pipe(map((result) => result?.data?.items || []));
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  search(): void {
    const formValues = this.appliedFilters;

    const number = formValues.number?.trim();
    const statusRaw = formValues.status;
    const status =
      statusRaw !== null && statusRaw !== undefined ? (typeof statusRaw === 'object' ? statusRaw.value : statusRaw) : undefined;

    const issuedDateFrom = formValues.issuedDateFrom ? this.formatDateForApi(formValues.issuedDateFrom) : undefined;
    const issuedDateTo = formValues.issuedDateTo ? this.formatDateForApi(formValues.issuedDateTo) : undefined;

    const remitterRef = formValues.remitterReference;
    const remitterReference = typeof remitterRef === 'string' ? remitterRef : remitterRef?.id;
    const consigneeRef = formValues.consigneeReference;
    const consigneeReference = typeof consigneeRef === 'string' ? consigneeRef : consigneeRef?.id;

    const command: RemissionGuideSearchCommand = {
      number: number || undefined,
      status: status,
      remitterReference: remitterReference || undefined,
      consigneeReference: consigneeReference || undefined,
      issuedDateFrom: issuedDateFrom,
      issuedDateTo: issuedDateTo,
    };

    this.startLoading();
    this.proxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          this.remissionGuides.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.remissionGuides.set([]);
          this.totalRecords = 0;
        },
      });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters as RemissionGuideFilters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetail(id?: string): void {
    const queryParams = id ? { id } : {};
    this.router.navigate(['/remission-guide-details'], { queryParams });
  }

  getStatusLabel(status: RemissionGuideStatus): string {
    const statuses = this.remissionGuideStatus();
    return statuses[status] || status.toString();
  }

  getStatusSeverity(status: RemissionGuideStatus): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' | undefined {
    switch (status) {
      case RemissionGuideStatus.Draft:
        return 'secondary';
      case RemissionGuideStatus.Delivered:
        return 'success';
      default:
        return 'secondary';
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
